/**
 * useDictation — press once, talk, and each sentence lands in the draft.
 *
 * Miles, 2026-09-09: "dictation keeps occurring and landing until user clicks
 * again the second time or sends the message. must be an extremely great user
 * experience."
 *
 * So the microphone is a TOGGLE, not a hold: one press opens it and it stays
 * open. Each time the speaker pauses, the piece they just said is written down
 * and inserted into the draft, and it keeps listening for the next one. It ends
 * on a second press, on send, or on its own after a long silence.
 *
 * WHERE THE WORDS COME FROM. Two streams, and only one of them is trusted. The
 * browser's own recognition (where it exists) drives the live words in the
 * strip so the speaker can see they are being heard — instant, and noticeably
 * worse on names. What actually lands in the draft always comes back from the
 * transcription tier, per pause. That split is the whole design: responsive
 * because of the first, accurate because of the second.
 *
 * WHY IT FEELS INSTANT ANYWAY. Sentence n is being transcribed while the
 * speaker is saying n+1, so the wait per sentence is one short round trip that
 * overlaps with talking, rather than one long one at the end.
 *
 * The insert goes through the composer's own idiom (focus + Range +
 * execCommand('insertText')) so the draft, its HTML twin and both persistence
 * layers update exactly as if the words had been typed. The composer is an
 * uncontrolled contentEditable; writing its state directly is the 2026-08-08
 * owner repro.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeDictation } from '../services/dictation/client';
import { startInterim, type InterimHandle } from '../services/dictation/interim';
import { startRecorder, type RecorderFailure, type RecorderHandle } from '../services/dictation/recorder';
import {
  concatFrames,
  durationMs,
  encodeWav,
  toBase64,
} from '../services/dictation/wavEncoder';
import {
  frameRms,
  isWorthSending,
  judgeFrame,
  newSegmenterState,
  type SegmenterState,
} from '../services/dictation/segmenter';

export type DictationPhase = 'idle' | 'requesting' | 'listening';

/**
 * What changes many times a second, kept OUT of React state on purpose.
 *
 * The recorder delivers a frame about twelve times a second, and the browser's
 * recognizer emits partial words nearly as often. Putting either in state would
 * re-render the whole chat shell — nine thousand lines and every message row —
 * on each one, to move a 3-pixel bar. So the fast values live in a ref that the
 * strip reads inside its own animation frame, and only phase, transcribing and
 * the notice (all rare) are state the host sees.
 */
export interface DictationLive {
  /** 0..1 loudness for the meter. */
  level: number;
  /** The browser's live partial words, or ''. Display only. */
  interim: string;
}

export interface DictationView {
  phase: DictationPhase;
  /** True while at least one segment is being written down. */
  transcribing: boolean;
  /** When the microphone opened, so the strip can run its own clock. */
  startedAt: number | null;
  /** The fast-changing values, read by the strip per animation frame. */
  live: React.MutableRefObject<DictationLive>;
  /** A D9 line to show, or null. */
  notice: string | null;
}

export interface UseDictationArgs {
  /** The thread being dictated into — the server resolves its names from this. */
  threadId: string | null | undefined;
  language: 'en' | 'es';
  /** Insert finished words into the draft. Returns false if it could not. */
  insertText: (text: string) => boolean;
  /** D9 copy for a failure this hook can hit locally. */
  noticeFor: (key: 'mic-blocked' | 'mic-unavailable' | 'transcribe-failed' | 'no-speech') => string;
}

export interface UseDictationResult extends DictationView {
  /** Press the microphone: starts, or stops if already listening. */
  toggle: () => void;
  /** End it without a press — the send path calls this. */
  stop: () => void;
  dismissNotice: () => void;
}

export function useDictation(args: UseDictationArgs): UseDictationResult {
  const [phase, setPhase] = useState<DictationPhase>('idle');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [inFlight, setInFlight] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const live = useRef<DictationLive>({ level: 0, interim: '' });

  const recorderRef = useRef<RecorderHandle | null>(null);
  const interimRef = useRef<InterimHandle | null>(null);
  const framesRef = useRef<Float32Array[]>([]);
  const stateRef = useRef<SegmenterState | null>(null);
  /** Bumped on every stop; a segment that returns from an ended session is dropped. */
  const runRef = useRef(0);
  const argsRef = useRef(args);
  argsRef.current = args;

  const teardown = useCallback(() => {
    runRef.current += 1;
    recorderRef.current?.stop();
    recorderRef.current = null;
    interimRef.current?.stop();
    interimRef.current = null;
    framesRef.current = [];
    stateRef.current = null;
    live.current = { level: 0, interim: '' };
    setPhase('idle');
    setStartedAt(null);
  }, []);

  /** Send one finished piece of speech and put its words in the draft. */
  const flush = useCallback((run: number) => {
    const frames = framesRef.current;
    framesRef.current = [];
    if (frames.length === 0) return;
    const samples = concatFrames(frames);
    if (!isWorthSending(durationMs(samples.length))) return;
    const threadId = String(argsRef.current.threadId || '');
    const audio = toBase64(encodeWav(samples));

    setInFlight((n) => n + 1);
    void transcribeDictation({ threadId, audio, language: argsRef.current.language })
      .then((response) => {
        // A segment that comes back after the person stopped is discarded: they
        // are done, and words appearing in the box afterwards would be a ghost.
        if (run !== runRef.current) return;
        if (response.ok && response.text.trim()) {
          argsRef.current.insertText(response.text.trim());
          // The real words are in the draft now; the browser's guess of the
          // same sentence would only sit there contradicting them.
          live.current.interim = '';
          return;
        }
        // Silence is not a failure and says nothing; anything else does.
        if (response.reason === 'no-speech') return;
        setNotice(response.notice || argsRef.current.noticeFor('transcribe-failed'));
      })
      .catch(() => {
        if (run !== runRef.current) return;
        setNotice(argsRef.current.noticeFor('transcribe-failed'));
      })
      .finally(() => setInFlight((n) => Math.max(0, n - 1)));
  }, []);

  const start = useCallback(async () => {
    if (phase !== 'idle') return;
    setNotice(null);
    setPhase('requesting');
    const run = runRef.current + 1;
    runRef.current = run;

    const begun = await startRecorder({
      onFrame: (samples) => {
        if (run !== runRef.current) return;
        const now = Date.now();
        if (!stateRef.current) stateRef.current = newSegmenterState(now);
        const rms = frameRms(samples);
        // A ref, not state: twelve renders a second of the whole chat shell to
        // move a meter bar is the wrong trade (see DictationLive).
        live.current.level = Math.min(1, rms * 12);
        framesRef.current.push(samples);
        const { next, action } = judgeFrame(stateRef.current, rms, now);
        stateRef.current = next;
        if (action.kind === 'cut') {
          flush(run);
        } else if (action.kind === 'stop') {
          flush(run);
          teardown();
        }
      },
      onLost: () => {
        if (run !== runRef.current) return;
        flush(run);
        teardown();
      },
    });

    if (run !== runRef.current) {
      // Stopped while the browser was still asking for permission.
      begun.handle?.stop();
      return;
    }
    if (!begun.ok || !begun.handle) {
      const failure: RecorderFailure = begun.failure || 'failed';
      setPhase('idle');
      setNotice(argsRef.current.noticeFor(
        failure === 'denied' ? 'mic-blocked'
          : failure === 'unavailable' ? 'mic-unavailable'
            : 'transcribe-failed',
      ));
      return;
    }

    recorderRef.current = begun.handle;
    stateRef.current = newSegmenterState(Date.now());
    interimRef.current = startInterim(argsRef.current.language, (words) => {
      if (run === runRef.current) live.current.interim = words;
    });
    setStartedAt(Date.now());
    setPhase('listening');
  }, [phase, flush, teardown]);

  const stop = useCallback(() => {
    if (recorderRef.current || phase !== 'idle') {
      // Whatever was said before the press still lands — stopping must never
      // throw away a sentence somebody had already finished.
      flush(runRef.current);
      teardown();
    }
  }, [phase, flush, teardown]);

  const toggle = useCallback(() => {
    if (phase === 'idle') void start();
    else stop();
  }, [phase, start, stop]);

  // The surface going away closes the microphone. Without this, a route change
  // mid-dictation would leave the recording indicator lit.
  useEffect(() => () => { teardown(); }, [teardown]);

  return {
    phase,
    transcribing: inFlight > 0,
    startedAt,
    live,
    notice,
    toggle,
    stop,
    dismissNotice: () => setNotice(null),
  };
}
